class CartDrawerSection extends HTMLElement {
  cartUpdateUnsubscriber = undefined;
  constructor() {
    super();

    this.cartType = this.dataset.cartType;
    this.drawerClass = "wt-cart__drawer";
    this.drawer = this.querySelector(`.${this.drawerClass}`);
    this.classDrawerActive = `${this.drawerClass}--open`;
    this.pageOverlayClass = "page-overlay-cart";
    this.activeOverlayBodyClass = `${this.pageOverlayClass}-on`;
    this.body = document.body;
    this.triggerQuery = [
      `.wt-cart__trigger`,
      `.wt-cart__back-link`,
      `.${this.pageOverlayClass}`,
    ].join(", ");
    this.triggers = () => document.querySelectorAll(this.triggerQuery);
    this.isOpen = false;
    this.isCartPage = window.location.pathname === window.routes.cart_url;
    this.closeButton = () => this.querySelector(".wt-cart__drawer__close");
    this.mainTrigger = () => document.querySelector(".wt-cart__trigger");
    this.sessionKey = "wt-cart-drawer-open";
    this.toggleEelements = () =>
      this.querySelectorAll(this.dataset.toggleTabindex);
  }

  connectedCallback() {
    if (typeof this.hypestPruneEmptyCrossSell === "function") this.hypestPruneEmptyCrossSell();
    if (typeof this.hypestFixCrossSellLayout === "function") this.hypestFixCrossSellLayout();

    if (this.cartType === "page" || this.isCartPage) {
      document.addEventListener("cart-drawer:refresh", (e) => this.refreshCartDrawer(e));
      return;
    }

    this.init();
    const subscribeFn = window.subscribe || (typeof subscribe === "function" ? subscribe : undefined);
    const pubSubEvents = window.PUB_SUB_EVENTS || (typeof PUB_SUB_EVENTS !== "undefined" ? PUB_SUB_EVENTS : undefined);
    if (subscribeFn && pubSubEvents) {
      this.cartUpdateUnsubscriber = subscribeFn(pubSubEvents.cartUpdate, () => {
        if (this.isOpen) {
          setTabindex(this.toggleEelements(), "0");
          this.closeButton()?.focus();
        }
      });
    }
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
  }

  getFocusableElements() {
    const focusableElementsSelector =
      "button, [href], input, select, [tabindex]";
    const focusableElements = () =>
      Array.from(this.querySelectorAll(focusableElementsSelector)).filter(
        (el) => !el.hasAttribute("disabled") && el.tabIndex >= 0,
      );

    return {
      focusableElements,
      first: focusableElements()[0],
      last: focusableElements()[focusableElements().length - 1],
    };
  }

  temporaryHideFocusVisible() {
    document.body.classList.add("no-focus-visible");
  }

  onToggle() {
    if (this.hasAttribute("open")) {
      this.removeAttribute("open");
      this.isOpen = false;
      this.mainTrigger()?.focus();
      this.temporaryHideFocusVisible();
      setTabindex(this.toggleEelements(), "-1");
    } else {
      this.setAttribute("open", "");
      this.isOpen = true;
      this.closeButton()?.focus();
      this.temporaryHideFocusVisible();
      setTabindex(this.toggleEelements(), "0");
    }
  }

  persistState() {
    try {
      if (this.isOpen) {
        sessionStorage.setItem(this.sessionKey, "1");
      } else {
        sessionStorage.removeItem(this.sessionKey);
      }
    } catch (e) {
      /* ignore */
    }
  }

  toggleDrawerClasses() {
    this.onToggle();
    this.drawer.classList.toggle(this.classDrawerActive);
    if (this.isOpen) {
      this.body.classList.add(this.activeOverlayBodyClass);
    } else {
      this.body.classList.remove(this.activeOverlayBodyClass);
    }
    this.persistState();
  }

  init() {
    this.addEventListener("keydown", (e) => {
      const isTabPressed =
        e.key === "Tab" || e.keyCode === 9 || e.code === "Tab";
      const { first, last } = this.getFocusableElements();

      if (e.key === "Escape" || e.keyCode === 27 || e.code === "Escape") {
        if (this.isOpen) {
          this.toggleDrawerClasses();
        }
      }

      if (isTabPressed) {
        if (e.shiftKey && document.activeElement === first) {
          last.focus();
          e.preventDefault();
        } else if (!e.shiftKey && document.activeElement === last) {
          first.focus();
          e.preventDefault();
        }
      }
    });

    document.addEventListener("click", (e) => {
      const trigger = e.target.closest(this.triggerQuery);
      if (trigger) {
        e.preventDefault();
        this.toggleDrawerClasses();
      }
    });

    this.addEventListener("click", (e) => {
      if (e.target.classList.contains("wt-cart__drawer__close")) {
        e.preventDefault();
        this.toggleDrawerClasses();
      }

      const bundleButton = e.target.closest("[data-hypest-bundle-add]");
      if (bundleButton) {
        e.preventDefault();
        this.addBundleItem(bundleButton);
      }
    });

    try {
      sessionStorage.removeItem(this.sessionKey);
    } catch (e) {
      /* ignore */
    }

    document.addEventListener("cart-drawer:refresh", (e) => this.refreshCartDrawer(e));
  }


  hypestPruneEmptyCrossSell() {
    this.querySelectorAll(".wt-cart__cross-sell").forEach((el) => {
      const card = el.querySelector(".shoppable-product-card");
      const img = el.querySelector(
        ".shoppable-product-card img, .wt-cart__cross-sell__products img",
      );
      const products = el.querySelector(".wt-cart__cross-sell__products");
      const hasProductMarkup =
        products &&
        (products.querySelector(".shoppable-product-card") ||
          /shoppable-product-card/i.test(products.innerHTML || ""));
      // Do not use getBoundingClientRect — closed drawer reports 0 height.
      if (!card || !img || !hasProductMarkup) {
        el.setAttribute("data-hypest-cross-sell-empty", "1");
        el.remove();
      }
    });
  }

  hypestFixCrossSellLayout() {
    // Clamp swiper slides that inherited featured_image intrinsic width (2048px+)
    this.querySelectorAll(
      ".wt-cart__cross-sell .swiper-slide, .wt-cart__cross-sell .wt-slider__slide, .wt-cart__cross-sell [data-swiper-slide], .wt-cart__cross-sell .shoppable-product-card--cross-sell",
    ).forEach((el) => {
      el.style.maxWidth = "280px";
      el.style.width = "280px";
      el.style.overflow = "hidden";
    });
    this.querySelectorAll(".wt-cart__cross-sell img").forEach((img) => {
      img.style.maxWidth = "96px";
      img.style.maxHeight = "96px";
      img.style.width = "96px";
      img.style.height = "96px";
      img.style.objectFit = "cover";
    });
  }

  renderContents(parsedState, isClosedCart = true) {
    this.hideLoading();
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);
      sectionElement.innerHTML = this.getSectionInnerHTML(
        parsedState.sections[section.id],
        section.selector,
      );
    });

    // HYPEST 2026-09-14: outer <cart-drawer> keeps server-rendered wt-cart--empty
    // across AJAX replaces of #CartDrawer. Sync empty state from live line items /
    // footer so empty-cart chrome does not fight a filled drawer.
    const hasItems = Boolean(
      this.querySelector(
        "#CartDrawer-CartItems .wt-cart__list .wt-cart__item, #CartDrawer-CartItems .cart-item, .wt-cart__drawer__footer",
      ),
    );
    this.classList.toggle("wt-cart--empty", !hasItems);

    // HYPEST 2026-09-14c: prune empty / image-less beige cross-sell (AJAX + initial)
    this.hypestPruneEmptyCrossSell();
    this.hypestFixCrossSellLayout();
    requestAnimationFrame(() => { this.hypestPruneEmptyCrossSell(); this.hypestFixCrossSellLayout(); });
    setTimeout(() => { this.hypestPruneEmptyCrossSell(); this.hypestFixCrossSellLayout(); }, 400);

    const isSuccessfulAdd =
      parsedState &&
      parsedState.id !== undefined &&
      parsedState.status === undefined;

    if (isClosedCart && isSuccessfulAdd) {
      setTimeout(() => {
        this.toggleDrawerClasses();
        if (this.isOpen) {
          this.closeButton()?.focus();
        }
      });
    }
  }

  async addBundleItem(button) {
    if (button.getAttribute("aria-disabled") === "true") return;

    const status = button
      .closest("[data-hypest-cart-bundle]")
      ?.querySelector("[data-hypest-bundle-status]");

    const buttonVariantIds = (button.dataset.variantIds || button.dataset.variantId || "")
      .split(",")
      .map((variantId) => variantId.trim())
      .filter(Boolean);
    if (buttonVariantIds.length === 0) return;

    button.setAttribute("aria-disabled", "true");
    if (status) status.textContent = button.dataset.addingLabel || "Wordt toegevoegd";

    const offerName = button.dataset.offerName || "winkelwagen-aanbieding";
    const body = JSON.stringify({
      items: buttonVariantIds.map((variantId) => ({
        id: Number(variantId),
        quantity: 1,
        properties: { _aanbieding: offerName },
      })),
      sections: this.getSectionsToRender().map((section) => section.id),
      sections_url: window.location.pathname,
    });

    try {
      const response = await fetch(window.routes.cart_add_url, {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          "X-Requested-With": "XMLHttpRequest",
        },
        body,
      });
      const result = await response.json();

      if (!response.ok || result.status) {
        throw new Error(result.description || "Toevoegen is niet gelukt");
      }

      this.renderContents(result, false);
      const publishFn = window.publish || (typeof publish === "function" ? publish : undefined);
      const pubSubEvents = window.PUB_SUB_EVENTS || (typeof PUB_SUB_EVENTS !== "undefined" ? PUB_SUB_EVENTS : undefined);
      if (publishFn && pubSubEvents) {
        publishFn(pubSubEvents.cartUpdate, {
          source: "hypest-cart-bundle",
          productVariantId: buttonVariantIds[0],
          cartData: result,
        });
      }
    } catch (error) {
      button.setAttribute("aria-disabled", "false");
      if (status) status.textContent = error.message || button.dataset.errorLabel || "Toevoegen is niet gelukt";
    }
  }

  getSectionInnerHTML(html, selector = ".shopify-section") {
    return new DOMParser()
      .parseFromString(html, "text/html")
      .querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: "cart-drawer",
        selector: "#CartDrawer",
      },
      {
        id: "cart-icon-bubble",
      },
    ];
  }

  refreshCartDrawer(e) {
    this.showLoading();
    const sectionsToRender = this.getSectionsToRender();
    fetch(
      `${window.Shopify.routes.root}?sections=${sectionsToRender[0].id},${sectionsToRender[1].id}`,
    )
      .then((response) => response.json())
      .then((response) => {
        const parsedState = {
          sections: response,
        };
        this.renderContents(parsedState, false);
      })
      .catch((e) => {
        this.hideLoading();
        console.log(e);
      });
  }

  showLoading() {
    this.classList.add("wt-cart--loading");
  }

  hideLoading() {
    this.classList.remove("wt-cart--loading");
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define("cart-drawer", CartDrawerSection);

// HYPEST-STOREFRONT-14: CartItems comes from cart.js, a separate deferred file.
// When cart.js did not load (blocked request, flaky network, content blocker)
// a top-level `extends CartItems` threw a ReferenceError and aborted this file.
// Define the element only once CartItems exists; retry once on window load.
function defineCartDrawerItems() {
  if (customElements.get("cart-drawer-items")) return true;
  if (typeof CartItems !== "function") return false;

  class CartDrawerItems extends CartItems {
    getSectionsToRender() {
      return [
        {
          id: "CartDrawer",
          section: "cart-drawer",
          selector: ".drawer__inner",
        },
        {
          id: "cart-icon-bubble",
          section: "cart-icon-bubble",
          selector: ".shopify-section",
        },
      ];
    }

    connectedCallback() {
      const subscribeFn = window.subscribe || (typeof subscribe === "function" ? subscribe : undefined);
      const pubSubEvents = window.PUB_SUB_EVENTS || (typeof PUB_SUB_EVENTS !== "undefined" ? PUB_SUB_EVENTS : undefined);
      if (!subscribeFn || !pubSubEvents) return;

      this.cartUpdateUnsubscriber = subscribeFn(
        pubSubEvents.cartUpdate,
        (event) => {
          // HYPEST 2026-09-14: product-form / bundle already render the drawer via
          // CartDrawerSection.renderContents. Inherited CartItems.onCartUpdate
          // fetches main-cart-items (cart-page markup with critical-hidden) and
          // blanks line items while the header still shows "Your cart (N)".
          if (
            !event ||
            event.source === "cart-items" ||
            event.source === "product-form" ||
            event.source === "hypest-cart-bundle" ||
            event.source === "hypest-burgundy-cart-bundle"
          ) {
            return;
          }
          this.onCartUpdate();
        },
      );
    }

    onCartUpdate() {
      const drawer = this.closest("cart-drawer");
      drawer?.showLoading?.();
      fetch(`${routes.cart_url}?section_id=cart-drawer`)
        .then((response) => response.text())
        .then((responseText) => {
          const html = new DOMParser().parseFromString(responseText, "text/html");
          const source = html.querySelector("#CartDrawer");
          const dest = document.querySelector("#CartDrawer");
          if (source && dest) {
            dest.innerHTML = source.innerHTML;
          }
        })
        .catch((e) => {
          console.error(e);
        })
        .finally(() => {
          drawer?.hideLoading?.();
        });
    }
  }

  customElements.define("cart-drawer-items", CartDrawerItems);
  return true;
}

if (!defineCartDrawerItems()) {
  window.addEventListener("load", defineCartDrawerItems, { once: true });
}
